import { DEFAULT_TIERED_MIN, getDisplayPriceWithUnit, getInterval } from "@Containers/web-portal/utils/stripe";
import useI18n from "@i18n";
import { StripeSubscriptionPrice } from "@Shared/schemas/stripe/stripeSchemas";
import { createContext, ReactNode, useContext, useState } from "react";
import { usePayment } from "./payment-context";

interface QuantityContextType {
  extraQuantity: number;
  setExtraQuantity: (value: number) => void;
  quantityIncluded: number;
  totalQuantity: number;
  calculateMonthlyBreakdown: () => string;
}

const QuantityContext = createContext<QuantityContextType>({
  extraQuantity: 0,
  setExtraQuantity: () => {},
  quantityIncluded: DEFAULT_TIERED_MIN,
  totalQuantity: DEFAULT_TIERED_MIN,
  calculateMonthlyBreakdown: () => "",
});

export const useQuantityContext = () => useContext(QuantityContext);

interface QuantityProviderProps {
  children: ReactNode;
}

export const QuantityProvider = ({ children }: QuantityProviderProps) => {
  const [extraQuantity, setExtraQuantity] = useState(0);
  const { finalPrice, paymentDetails, tieredPricing, prices, selectedPriceId } = usePayment();
  const { i18n } = useI18n();

  const quantityIncluded = tieredPricing?.min || DEFAULT_TIERED_MIN;
  const totalQuantity = quantityIncluded + extraQuantity;

  // Get the selected price to determine the interval
  const selectedPriceData = prices.find((price) => price.id === selectedPriceId);
  const interval = selectedPriceData ? getInterval(selectedPriceData as StripeSubscriptionPrice) : "year";
  const isAnnual = interval === "year";

  const calculateMonthlyBreakdown = () => {
    // Get the correct word for seat/seats
    const seatText = totalQuantity === 1 ? i18n("payment.subscription.panel.text.seat") : i18n("payment.subscription.panel.text.seats");

    if (isAnnual) {
      const monthlyPrice = Math.ceil(finalPrice / 12 / totalQuantity);
      return i18n("payment.subscription.panel.text.monthly.breakdown", {
        price: `${getDisplayPriceWithUnit(monthlyPrice, paymentDetails.currency)} x ${totalQuantity} ${seatText} x 12 months`,
      });
    } else {
      // For monthly subscriptions, no need to divide by 12
      const monthlyPrice = Math.ceil(finalPrice / totalQuantity);
      return i18n("payment.subscription.panel.text.monthly.breakdown", {
        price: `${getDisplayPriceWithUnit(monthlyPrice, paymentDetails.currency)} x ${totalQuantity} ${seatText}`,
      });
    }
  };

  return (
    <QuantityContext.Provider
      value={{
        extraQuantity,
        setExtraQuantity,
        quantityIncluded,
        totalQuantity,
        calculateMonthlyBreakdown,
      }}
    >
      {children}
    </QuantityContext.Provider>
  );
};
